<template>
  <div class="background" :style="{ backgroundColor: isDarkMode ? '#0d0d0d' : '#e7e7e7' }">
    <div class="holder" ref="holder" v-show="dotsReady"></div>
    <!-- Debug overlay - remove in production -->
    <div v-if="showDebug" class="debug-overlay">
      <div>Grid Ready: {{ gridReady }}</div>
      <div>Fonts Loaded: {{ fontsLoaded }}</div>
      <div>Dots Created: {{ dotsCreated }}</div>
      <div>Layout Stable: {{ layoutStable }}</div>
      <div>Render Time: {{ renderTime }}ms</div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue'

export default {
  name: 'MovingDotsBackground',
  props: {
    isDarkMode: {
      type: Boolean,
      required: true
    },
    showDebug: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const holder = ref(null)
    const gs = 55
    
    // Debug state
    const gridReady = ref(false)
    const fontsLoaded = ref(false)
    const dotsCreated = ref(false)
    const dotsReady = ref(false)
    const layoutStable = ref(false)
    const renderTime = ref(0)

    // Strategy 1: Wait for CSS Grid layout completion
    const waitForGridLayout = () => {
      return new Promise((resolve) => {
        const checkLayout = () => {
          if (!holder.value) {
            requestAnimationFrame(checkLayout)
            return
          }

          const style = getComputedStyle(holder.value)
          const gridTemplateColumns = style.gridTemplateColumns
          const gridTemplateRows = style.gridTemplateRows
          
          // Check if grid has been calculated (not 'none')
          if (gridTemplateColumns !== 'none' && gridTemplateRows !== 'none') {
            // Verify actual grid dimensions
            const columns = gridTemplateColumns.split(' ').length
            const rows = gridTemplateRows.split(' ').length
            
            if (columns === gs && rows === gs) {
              gridReady.value = true
              resolve()
              return
            }
          }
          
          requestAnimationFrame(checkLayout)
        }
        
        checkLayout()
      })
    }

    // Strategy 2: Font loading detection
    const waitForFonts = () => {
      return new Promise((resolve) => {
        if ('fonts' in document) {
          document.fonts.ready.then(() => {
            fontsLoaded.value = true
            resolve()
          })
        } else {
          // Fallback for browsers without Font Loading API
          setTimeout(() => {
            fontsLoaded.value = true
            resolve()
          }, 100)
        }
      })
    }

    // Strategy 3: Layout stability detection
    const waitForLayoutStability = () => {
      return new Promise((resolve) => {
        let lastHeight = 0
        let stableCount = 0
        const requiredStableFrames = 3

        const checkStability = () => {
          if (!holder.value) {
            requestAnimationFrame(checkStability)
            return
          }

          const currentHeight = holder.value.offsetHeight
          
          if (currentHeight === lastHeight && currentHeight > 0) {
            stableCount++
            if (stableCount >= requiredStableFrames) {
              layoutStable.value = true
              resolve()
              return
            }
          } else {
            stableCount = 0
          }
          
          lastHeight = currentHeight
          requestAnimationFrame(checkStability)
        }
        
        checkStability()
      })
    }

    // Strategy 4: Progressive dot creation
    const createDotsProgressively = async () => {
      if (!holder.value) return

      const startTime = performance.now()
      holder.value.innerHTML = ''
      
      // Create DocumentFragment for better performance
      const fragment = document.createDocumentFragment()
      const batchSize = 200 // Create dots in batches
      
      for (let batch = 0; batch < Math.ceil((gs * gs) / batchSize); batch++) {
        const batchFragment = document.createDocumentFragment()
        const startIdx = batch * batchSize
        const endIdx = Math.min(startIdx + batchSize, gs * gs)
        
        for (let idx = startIdx; idx < endIdx; idx++) {
          const i = Math.floor(idx / gs)
          const j = idx % gs
          
          const dot = document.createElement('div')
          dot.classList.add('dot')
          dot.style.backgroundColor = props.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
          dot.style.gridColumn = j + 1
          dot.style.gridRow = i + 1
          dot.style.animationDelay = `${(i + j) * 0.1}s`
          
          batchFragment.appendChild(dot)
        }
        
        fragment.appendChild(batchFragment)
        
        // Yield control back to browser between batches
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      
      holder.value.appendChild(fragment)
      
      renderTime.value = Math.round(performance.now() - startTime)
      dotsCreated.value = true
    }

    // Strategy 5: Comprehensive initialization
    const initializeDots = async () => {
      try {
        // Wait for all prerequisites
        await Promise.all([
          waitForGridLayout(),
          waitForFonts(),
          waitForLayoutStability()
        ])
        
        // Ensure we're in the next tick
        await nextTick()
        
        // Additional safety: wait one more animation frame
        await new Promise(resolve => requestAnimationFrame(resolve))
        
        // Create dots progressively
        await createDotsProgressively()
        
        // Final verification: ensure dots are actually rendered
        await new Promise(resolve => requestAnimationFrame(resolve))
        
        // Make visible
        dotsReady.value = true
        
      } catch (error) {
        console.error('Dots initialization failed:', error)
        // Fallback: try simple creation after delay
        setTimeout(() => {
          createDotsSimple()
          dotsReady.value = true
        }, 200)
      }
    }

    // Fallback: Simple dot creation
    const createDotsSimple = () => {
      if (!holder.value) return

      holder.value.innerHTML = ''
      for (let i = 0; i < gs; i++) {
        for (let j = 0; j < gs; j++) {
          const dot = document.createElement('div')
          dot.classList.add('dot')
          dot.style.backgroundColor = props.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
          dot.style.gridColumn = j + 1
          dot.style.gridRow = i + 1
          dot.style.animationDelay = `${(i + j) * 0.1}s`
          holder.value.appendChild(dot)
        }
      }
      dotsCreated.value = true
    }

    onMounted(() => {
      initializeDots()
    })

    watch(() => props.isDarkMode, async () => {
      // Reset state
      dotsReady.value = false
      dotsCreated.value = false
      
      // Wait a frame then recreate
      await nextTick()
      await initializeDots()
    })

    return { 
      holder, 
      dotsReady,
      // Debug exports
      gridReady,
      fontsLoaded,
      dotsCreated,
      layoutStable,
      renderTime
    }
  }
}
</script>

<style scoped>
.background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  margin-top: calc(-1 * env(safe-area-inset-top));
  margin-bottom: calc(-1 * env(safe-area-inset-bottom));
  margin-left: calc(-1 * env(safe-area-inset-left));
  margin-right: calc(-1 * env(safe-area-inset-right));
  z-index: -10;
  pointer-events: none;
  transition: background-color 0.3s ease;
}

.holder {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(55, 1fr);
  grid-template-rows: repeat(55, 1fr);
  min-height: calc(100vh + env(safe-area-inset-top) + env(safe-area-inset-bottom));
  min-width: calc(100vw + env(safe-area-inset-left) + env(safe-area-inset-right));
  /* Enhanced performance hints */
  will-change: contents;
  contain: layout style paint;
  transform: translateZ(0);
  /* Ensure grid is properly calculated before becoming visible */
  opacity: 0;
  animation: fadeIn 0.3s ease-in-out 0.1s forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  justify-self: center;
  align-self: center;
  animation: moveDots 2s ease-in-out infinite alternate;
  /* Performance optimization */
  will-change: transform;
  contain: layout;
}

@keyframes moveDots {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(10px);
  }
}

.debug-overlay {
  position: fixed;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px;
  font-family: monospace;
  font-size: 12px;
  z-index: 1000;
  border-radius: 4px;
}

.debug-overlay div {
  margin: 2px 0;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .background {
    height: 100dvh;
    min-height: 100vh;
    top: -50px;
    bottom: -50px;
    left: -10px;
    right: -10px;
    width: calc(100vw + 20px);
    height: calc(100vh + 100px);
  }
  
  .holder {
    min-height: calc(100vh + 100px);
    min-width: calc(100vw + 20px);
  }
}
</style>